import { executeProjectSync } from "src/handlers/ProjectSync";
import { APPWRITE_DATABASE_ID, PROJECT_ACTIVITY_TABLE_ID, client } from "src/handlers/Appwrite";
import { getProjectId } from "src/handlers/Sync";

// Phase H2 \u2014 activity log client helpers.
//
// `logActivity` writes an audit row; it's intentionally fire-and-forget so
// the hot write path (syncSection / syncPageData) never blocks on it. Any
// network or permission error is swallowed and logged via console.warn.
//
// `listActivity` fetches the most recent rows for a project. Unlike
// logActivity, it surfaces errors to the caller so the UI can show an
// empty/errored state.

/**
 * Canonical activity row shape. Aligns with what the function returns.
 * @typedef {{
 *   $id: string,
 *   projectId: string,
 *   userId: string,
 *   actor: string,
 *   kind: string,
 *   payloadJson: string,
 *   createdAtMs: number
 * }} ActivityRow
 */

const SAFE_LOG_FAILURES = new WeakSet();

function warnOnceKey(error) {
  // Cheap dedupe so a batch of failing writes doesn't spam the console.
  if (!error || SAFE_LOG_FAILURES.has(error)) return false;
  try {
    SAFE_LOG_FAILURES.add(error);
  } catch {
    // Errors produced by some runtimes aren't object-like; fall through.
  }
  return true;
}

/**
 * Pure helper: build the request body we'd send to /activity/log. Exposed
 * so callers (and tests) can construct payloads without calling the network.
 *
 * @param {{ projectId?: string, kind: string, payload?: unknown }} args
 * @returns {{ projectId: string, kind: string, payload?: unknown } | null}
 *          null when we can't produce a valid payload (no projectId/kind).
 */
export function buildActivityBody({ projectId, kind, payload }) {
  const pid = String(projectId || "").trim();
  const knd = String(kind || "").trim();
  if (!pid || !knd) return null;
  const body = { projectId: pid, kind: knd };
  if (payload !== undefined) body.payload = payload;
  return body;
}

/**
 * Fire-and-forget write. Always resolves; never rejects. Logs a warning on
 * failure so dev builds can still see the error. Accepts explicit projectId
 * or falls back to the active project via `getProjectId()`.
 *
 * @param {{ projectId?: string, kind: string, payload?: unknown }} args
 * @returns {Promise<{ ok: boolean, row?: ActivityRow, error?: string }>}
 */
export async function logActivity(args = {}) {
  try {
    const projectId = args.projectId || getProjectId();
    const body = buildActivityBody({ ...args, projectId });
    if (!body) {
      // Nothing to log (no active project or bogus kind) \u2014 benign skip.
      return { ok: false, error: "missing projectId or kind" };
    }
    const response = await executeProjectSync("/activity/log", body);
    return response;
  } catch (error) {
    if (warnOnceKey(error)) {
      // eslint-disable-next-line no-console
      console.warn("activity: log failed", error?.message || error);
    }
    return { ok: false, error: error?.message || "log failed" };
  }
}

/**
 * Fetch the most recent activity for a project. Unlike logActivity this
 * surfaces the error to the caller so UI can show an error state.
 *
 * @param {{ projectId?: string, limit?: number }} args
 * @returns {Promise<ActivityRow[]>}
 */
export async function listActivity(args = {}) {
  const projectId = args.projectId || getProjectId();
  if (!projectId) return [];
  const body = {
    projectId,
    ...(Number.isFinite(Number(args.limit)) ? { limit: Number(args.limit) } : {})
  };
  const response = await executeProjectSync("/activity/list", body);
  return Array.isArray(response?.rows) ? response.rows : [];
}

/**
 * Phase J3: subscribe to realtime `project_activity` inserts for a project.
 * `callback` is invoked with the new row whenever Appwrite streams a create
 * event. Returns an `unsubscribe` function; no-ops when there's no project
 * or the Appwrite client lacks a subscribe method (e.g. SSR).
 *
 * @param {{ projectId?: string }} args
 * @param {(row: ActivityRow) => void} callback
 * @returns {() => void}
 */
export function subscribeActivity(args = {}, callback) {
  const projectId = args.projectId || getProjectId();
  if (!projectId) return () => {};
  if (typeof client?.subscribe !== "function") return () => {};
  if (typeof callback !== "function") return () => {};

  const channel = `databases.${APPWRITE_DATABASE_ID}.tables.${PROJECT_ACTIVITY_TABLE_ID}.rows`;
  try {
    return client.subscribe(channel, (response) => {
      const row = response?.payload;
      if (!row || row.projectId !== projectId) return;
      const isCreate = (response?.events || []).some((event) =>
        typeof event === "string" && event.endsWith(".create")
      );
      if (!isCreate) return;
      callback({
        $id: row.$id,
        projectId: row.projectId,
        userId: row.userId || "",
        actor: row.actor || "",
        kind: row.kind || "",
        payloadJson: row.payloadJson || "",
        createdAtMs: Number(row.createdAtMs || 0)
      });
    });
  } catch {
    return () => {};
  }
}

/**
 * Pure helper: parse the stringified `payloadJson` on an activity row.
 * Safe against invalid input; always returns an object (never throws).
 *
 * @param {ActivityRow | { payloadJson?: string }} row
 * @returns {Record<string, unknown>}
 */
export function readActivityPayload(row) {
  const raw = row?.payloadJson;
  if (typeof raw !== "string" || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

/**
 * Pure helper: short English summary of an activity row suitable for the
 * Dashboard feed. Falls back to the raw `kind` for unknown kinds.
 *
 * @param {ActivityRow} row
 * @returns {string}
 */
export function describeActivity(row) {
  if (!row) return "";
  const payload = readActivityPayload(row);
  const kind = row.kind || "";
  switch (kind) {
    case "deviceSection": {
      const deviceType = payload.deviceType ? String(payload.deviceType) : "device";
      const tabId = Number.isFinite(Number(payload.tabId)) ? Number(payload.tabId) + 1 : "";
      const hostname = payload.hostname ? String(payload.hostname) : "";
      const label = hostname
        ? hostname
        : tabId
          ? `${deviceType} #${tabId}`
          : deviceType;
      // Phase I1: prefer the one-line diff summary when present.
      if (payload.summary) {
        return `on ${label}: ${payload.summary}`;
      }
      const sectionKey = payload.sectionKey ? String(payload.sectionKey) : "section";
      return `updated ${label} \u00b7 ${sectionKey}`;
    }
    case "page":
      return `updated page ${payload.pageKey || ""}`.trim();
    case "deploy":
      return `deployed ${payload.hostname || payload.deviceKey || "a device"}`;
    case "deviceAdd":
      return `added ${payload.deviceType || "device"} ${payload.hostname || ""}`.trim();
    case "deviceRemove":
      return `removed ${payload.deviceType || "device"} ${payload.hostname || ""}`.trim();
    case "projectRename":
      return `renamed project to "${payload.name || ""}"`;
    default:
      return kind || "updated project";
  }
}

/**
 * Phase K4: break `describeActivity(row)` into a short headline + an
 * optional longer detail line for the Dashboard feed. The detail is
 * populated only when the row has a rich `payload.summary` (Phase I1) so
 * simple rows still render on a single line.
 *
 * @param {ActivityRow} row
 * @returns {{ headline: string, detail: string }}
 */
export function describeActivityForFeed(row) {
  const sentence = describeActivity(row);
  if (!row) return { headline: sentence, detail: "" };
  const payload = readActivityPayload(row);
  // Only deviceSection rows carry a diff summary today; other kinds render
  // as a single-line headline.
  if (row.kind === "deviceSection" && typeof payload.summary === "string" && payload.summary.length > 0) {
    const deviceType = payload.deviceType ? String(payload.deviceType) : "device";
    const hostname = payload.hostname ? String(payload.hostname) : "";
    const tabId = Number.isFinite(Number(payload.tabId)) ? Number(payload.tabId) + 1 : "";
    const label = hostname
      ? hostname
      : tabId
        ? `${deviceType} #${tabId}`
        : deviceType;
    return { headline: `edited ${label}`, detail: String(payload.summary) };
  }
  return { headline: sentence, detail: "" };
}
